import type { ColumnDef } from '@tanstack/react-table'
import type { IVoucher } from '@/entities/voucher'
import type { PaginationConfigInput } from '@/shared/ui/data-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { VoucherDeleteButton } from '@/features/voucher/voucher-delete-button'
import { VoucherStatusToggle } from '@/features/voucher/voucher-status-toggle'
import { Badge } from '@/shared/ui/badge'
import { DataTable } from '@/shared/ui/data-table'

interface VoucherListTableProps {
  vouchers: IVoucher[]
  loading: boolean
  pagination: PaginationConfigInput
  onPageChange: (page: number, pageSize: number) => void
  onEdit: (voucher: IVoucher) => void
}

function VoucherTypeBadge({ type }: { type: IVoucher['type'] }) {
  const { t } = useTranslation()
  return (
    <Badge variant={type === 'TOUR' ? 'default' : 'secondary'}>
      {type === 'TOUR' ? t('voucher.type.tour') : t('voucher.type.poi')}
    </Badge>
  )
}

function formatVoucherDate(value: string): string {
  const [year, month, day] = value.split('-')
  return year && month && day ? `${day}/${month}/${year}` : value
}

export function VoucherListTable({
  vouchers,
  loading,
  pagination,
  onPageChange,
  onEdit,
}: VoucherListTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<IVoucher>[]>(
    () => [
      {
        id: 'code',
        header: t('voucher.code'),
        cell: ({ row }) => (
          <span className="font-mono text-sm font-medium text-zinc-900">{row.original.code}</span>
        ),
      },
      {
        id: 'name',
        header: t('voucher.name'),
        cell: ({ row }) => (
          <div className="min-w-[160px]">
            <p className="text-sm font-medium text-zinc-900">{row.original.name}</p>
          </div>
        ),
      },
      {
        id: 'type',
        header: t('common.type'),
        cell: ({ row }) => <VoucherTypeBadge type={row.original.type} />,
      },
      {
        id: 'discountPercent',
        header: t('voucher.discountPercent'),
        cell: ({ row }) => (
          <span className="font-medium text-emerald-600">{row.original.discountPercent}%</span>
        ),
      },
      {
        id: 'quantity',
        header: t('voucher.quantity'),
        cell: ({ row }) => row.original.quantity.toLocaleString(),
      },
      {
        id: 'issuedCount',
        header: t('voucher.issuedCount'),
        cell: ({ row }) => row.original.issuedCount.toLocaleString(),
      },
      {
        id: 'usedCount',
        header: t('voucher.usedCount'),
        cell: ({ row }) => row.original.usedCount.toLocaleString(),
      },
      {
        id: 'validity',
        header: t('voucher.validity'),
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-sm text-zinc-600">
            <span>{formatVoucherDate(row.original.startDate)}</span>
            <span className="mx-1 text-zinc-400">→</span>
            <span>{formatVoucherDate(row.original.endDate)}</span>
          </div>
        ),
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <VoucherStatusToggle voucherId={row.original.id} status={row.original.status} />
        ),
      },
      {
        id: 'actions',
        size: 64,
        header: () => null,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <VoucherDeleteButton voucherId={row.original.id} />
          </div>
        ),
      },
    ],
    [t],
  )

  return (
    <DataTable
      columns={columns}
      data={vouchers}
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: onPageChange,
      }}
      onRowClick={onEdit}
    />
  )
}
